// Dashboard.js
import { motion } from "framer-motion";
import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  BarChart,
  Bar,
  CartesianGrid,
} from "recharts";
import { useSelector } from "react-redux";
import {
  FaTruck,
  FaUserCheck,
  FaShippingFast,
  FaClipboardList,
  FaCalendarCheck,
  FaMoneyBillWave,
  FaHandHoldingUsd,
} from "react-icons/fa";
import NewMiniDrawer from "../../components/navMenu/menu-nav";
import driverService from "../../service/driver.service";
import freightService from "../../service/freight.service";
import BrazilFreightOriginMap, {
  aggregateDestinationUfCounts,
  aggregateOriginUfCounts,
} from "./brazil-freight-origin-map";

const StatCard = ({ title, value, icon: Icon, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    whileHover={{ scale: 1.03 }}
    style={{
      flex: "1 1 240px",
      padding: "20px",
      borderRadius: "16px",
      backgroundColor: "#ffffff",
      boxShadow: "0 6px 12px rgba(0,0,0,0.06)",
      display: "flex",
      alignItems: "center",
      gap: "16px",
      borderLeft: `6px solid ${color}`,
      cursor: "pointer",
    }}
  >
    <div
      style={{
        backgroundColor: `${color}22`,
        padding: "12px",
        borderRadius: "50%",
        color: color,
        fontSize: "24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon />
    </div>
    <div>
      <div style={{ fontSize: "14px", color: "#555" }}>{title}</div>
      <div style={{ fontSize: "26px", fontWeight: "bold", color }}>{value}</div>
    </div>
  </motion.div>
);

const COLORS = ["#0088FE", "#FF8042"];

/** Paleta Fortio (tela de frete: freight.css, ProgressSteps, bordas MUI) */
const FORTIO = {
  navy: "#012443",
  navyDark: "#001f3f",
  navyGreen: "#043124",
  navyMid: "#1a4a63",
  orange: "#FF3100",
  orangeDeep: "#d62e04",
  slate: "#344d66",
};

const BAR_COLORS = [
  FORTIO.navy,
  FORTIO.orange,
  FORTIO.slate,
  FORTIO.navyGreen,
  FORTIO.navyDark,
  FORTIO.orangeDeep,
  "#5a7a8c",
  FORTIO.navy,
];

const PERIOD_OPTIONS = [
  { value: "today", label: "Hoje" },
  { value: "week", label: "Essa semana"},
  { value: "month", label: "Este mes" },
  { value: "year", label: "Este ano" },
];

function toDateSafe(rawDate) {
  if (rawDate === undefined || rawDate === null || rawDate === "") {
    return null;
  }
  const d = rawDate.toDate ? rawDate.toDate() : new Date(rawDate);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isDateInPeriod(date, period, now = new Date()) {
  if (!date) return false;

  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (period === "today") {
    const endToday = new Date(startToday);
    endToday.setHours(23, 59, 59, 999);
    return date >= startToday && date <= endToday;
  }

  if (period === "week") {
    const day = now.getDay();
    const diffToMonday = day === 0 ? 6 : day - 1;
    const startWeek = new Date(startToday);
    startWeek.setDate(startWeek.getDate() - diffToMonday);
    const endWeek = new Date(startWeek);
    endWeek.setDate(endWeek.getDate() + 6);
    endWeek.setHours(23, 59, 59, 999);
    return date >= startWeek && date <= endWeek;
  }

  if (period === "month") {
    return (
      date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth()
    );
  }

  return date.getFullYear() === now.getFullYear();
}

/** Tipos de veículo marcados no frete (campo vehicle.typeVehicle do documento). */
function getSelectedVehicleTypeLabels(vehicle) {
  if (!vehicle?.typeVehicle) return [];
  const tv = vehicle.typeVehicle;
  if (Array.isArray(tv.dados)) {
    return tv.dados.filter((d) => d.selected).map((d) => d.name).filter(Boolean);
  }
  if (Array.isArray(tv.federal)) {
    return tv.federal.filter((d) => d.selected).map((d) => d.name).filter(Boolean);
  }
  return [];
}

function aggregateTripsByVehicleType(freightDocs) {
  const counts = {};
  freightDocs.forEach((doc) => {
    const labels = getSelectedVehicleTypeLabels(doc.data().vehicle);
    if (labels.length === 0) {
      counts["Não informado"] = (counts["Não informado"] || 0) + 1;
    } else {
      labels.forEach((name) => {
        counts[name] = (counts[name] || 0) + 1;
      });
    }
  });
  return Object.entries(counts)
    .map(([tipo, viagens]) => ({ tipo, viagens }))
    .sort((a, b) => b.viagens - a.viagens);
}

/** Valor numérico de freight.valueFreightage (aceita número ou texto BR). */
function parseFreightValue(raw) {
  if (raw === undefined || raw === null || raw === "") {
    return 0;
  }
  if (typeof raw === "number" && !Number.isNaN(raw)) {
    return raw;
  }
  const s = String(raw).trim().replace(/\s/g, "");
  const normalized = s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s;
  const n = parseFloat(normalized);
  return Number.isNaN(n) ? 0 : n;
}

/** Soma freight.valueFreightage dos fretes do periodo selecionado. */
function sumFreightValueByPeriod(freightDocs, period) {
  let total = 0;
  freightDocs.forEach((doc) => {
    const data = doc.data();
    const d = toDateSafe(data.createData);
    if (!isDateInPeriod(d, period)) {
      return;
    }
    total += parseFreightValue(data.freight?.valueFreightage);
  });
  return total;
}

/**
 * Soma driver.valueNegotiated (valor negociado pago ao motorista) para fretes
 * finalizados (code 07) com history.dateFinished no periodo selecionado.
 */
function sumNegotiatedDriverFinalizedByPeriod(freightDocs, period) {
  let total = 0;
  freightDocs.forEach((doc) => {
    const data = doc.data();
    if (data.status?.code !== "07") {
      return;
    }
    const d = toDateSafe(data.history?.dateFinished);
    if (!isDateInPeriod(d, period)) {
      return;
    }
    total += parseFreightValue(data.driver?.valueNegotiated);
  });
  return total;
}

async function countDeliveryPointsByFreight(freightDocs) {
  const entries = await Promise.all(
    freightDocs.map(async (doc) => {
      const [deliveredSnap, pendingSnap] = await Promise.all([
        freightService.freightDelivered(doc.id),
        freightService.freightNotDelivered(doc.id),
      ]);
      return [doc.id, { delivered: deliveredSnap.size, pending: pendingSnap.size }];
    })
  );
  return Object.fromEntries(entries);
}

function formatBrl(value) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

const DashboardHome = () => {

    const [countDriver, setCountDriver] = useState(0);
    const [countFreight] = useState(0);
    const [countDriverInTravel, setCountDriverInTravel] = useState(0);
    const [countDelivery, setCountDelivery] = useState(0);
    const [countMakeDelivery, setCountMakeDelivery] = useState(0);

    const [countFreightPend, setCountFreightPend] = useState(0);
    const [countFreightHired, setCountFreightHired] = useState(0);
    const [countFreightInTravel, setCountFreightInTravel] = useState(0);
    const [countFreighFinaly, setCountFreightFinaly] = useState(0);
    const [tripsByVehicleType, setTripsByVehicleType] = useState([]);
    const [loadingTripsByVehicleType, setLoadingTripsByVehicleType] = useState(true);
    const [countTripsThisYear, setCountTripsThisYear] = useState(0);
    const [totalFreightValueThisYear, setTotalFreightValueThisYear] = useState(0);
    const [totalPaidDriverThisYear, setTotalPaidDriverThisYear] = useState(0);
    const [originUfCounts, setOriginUfCounts] = useState({});
    const [destinationUfCounts, setDestinationUfCounts] = useState({});
    const [allFreightDocs, setAllFreightDocs] = useState([]);
    const [deliveryByFreightId, setDeliveryByFreightId] = useState({});
    const [dateFilter, setDateFilter] = useState("month");

    const user = useSelector(state => state.user);
    
    useEffect(() => {
        const fetchData = async () => {
          setLoadingTripsByVehicleType(true);
          try {
            const allDrivers = await driverService.getAllDriver(user.uidShipper);
            const allFreightsSnap = await freightService.getAllFreightsByShipper(user.uidShipper);

            setCountDriver(allDrivers);
            setAllFreightDocs(allFreightsSnap.docs);
            setDeliveryByFreightId(await countDeliveryPointsByFreight(allFreightsSnap.docs));
          } catch (error) {
            console.error("Erro ao buscar dados dos fretes e motoristas:", error);
          } finally {
            setLoadingTripsByVehicleType(false);
          }
        };
      
        fetchData();
      }, [user?.uidShipper]);

      const filteredFreightDocs = useMemo(() => {
        return allFreightDocs.filter((doc) => {
          const data = doc.data();
          const date = toDateSafe(data.createData);
          return isDateInPeriod(date, dateFilter);
        });
      }, [allFreightDocs, dateFilter]);

      useEffect(() => {
        const getStatus = (doc) => (doc.data().status?.describe || "").toLowerCase();
        const inTravel = filteredFreightDocs.filter((doc) =>
          getStatus(doc).includes("em transito")
        ).length;
        const hired = filteredFreightDocs.filter((doc) =>
          getStatus(doc).includes("contratado")
        ).length;
        const pendingValidation = filteredFreightDocs.filter((doc) =>
          getStatus(doc).includes("pendente de validação")
        ).length;
        const pending = filteredFreightDocs.filter((doc) =>
          getStatus(doc).includes("pendente de contratação")
        ).length;
        const finalized = filteredFreightDocs.filter((doc) => {
          const statusDesc = getStatus(doc);
          return statusDesc.includes("finalizado") || doc.data().status?.code === "07";
        }).length;

        const deliveryTotals = filteredFreightDocs.reduce(
          (acc, doc) => {
            const byFreight = deliveryByFreightId[doc.id] || { delivered: 0, pending: 0 };
            acc.delivered += byFreight.delivered;
            acc.pending += byFreight.pending;
            return acc;
          },
          { delivered: 0, pending: 0 }
        );

        setCountDriverInTravel(inTravel);
        setCountFreightInTravel(inTravel);
        setCountFreightHired(hired);
        setCountFreightPend(pending + pendingValidation);
        setCountFreightFinaly(finalized);
        setCountDelivery(deliveryTotals.delivered);
        setCountMakeDelivery(deliveryTotals.pending);
        setCountTripsThisYear(finalized);
        setTotalFreightValueThisYear(sumFreightValueByPeriod(filteredFreightDocs, dateFilter));
        setTotalPaidDriverThisYear(
          sumNegotiatedDriverFinalizedByPeriod(filteredFreightDocs, dateFilter)
        );
        setOriginUfCounts(aggregateOriginUfCounts(filteredFreightDocs));
        setDestinationUfCounts(aggregateDestinationUfCounts(filteredFreightDocs));
        setTripsByVehicleType(aggregateTripsByVehicleType(filteredFreightDocs));
      }, [filteredFreightDocs, deliveryByFreightId, dateFilter]);
      


 


    const estatisticas = {
        motoristasCadastrados: countDriver,
        fretesDisponiveis: countFreight,
        caminhoesEmViagem: countDriverInTravel,
        entregasEfetuadas: countDelivery,
        entregasAFazer: countMakeDelivery,
        fretesPendentes: countFreightPend,
        fretesContratados: countFreightHired,
        fretesEmViagem: countFreightInTravel,
        fretesFinalizados: countFreighFinaly,
        viagensEsteAno: countTripsThisYear,
        valorTotalFretesEsteAno: formatBrl(totalFreightValueThisYear),
        pagoAoMotoristaEsteAno: formatBrl(totalPaidDriverThisYear),
      };
    
      const kmPorMes = [
        { mes: "Jan", km: 18000 },
        { mes: "Fev", km: 22000 },
        { mes: "Mar", km: 27000 },
        { mes: "Abr", km: 25000 },
        { mes: "Mai", km: 30000 },
        { mes: "Jun", km: 28000 },
        { mes: "Jul", km: 28000 },
        { mes: "Ago", km: 28000 },
        { mes: "Set", km: 28000 },
        { mes: "Out", km: 28000 },
        { mes: "Nov", km: 28000 },
        { mes: "Dez", km: 28000 },
      ];
    
      const pieData = [
        { name: "Efetuadas", value: estatisticas.entregasEfetuadas },
        { name: "A Fazer", value: estatisticas.entregasAFazer },
      ];

      const dataFretes = [
        { name: "Pendentes", value: estatisticas.fretesPendentes },
        { name: "Contratados", value: estatisticas.fretesContratados },
        { name: "Em Viagem", value: estatisticas.fretesEmViagem },
        { name: "Finalizados", value: estatisticas.fretesFinalizados },
      ];
      

  return (
   <>
    {useSelector((state) => state.usuarioLogado) > 0 ? (
        <NewMiniDrawer  
            divOpen={
                
                <div style={{ padding: "32px", fontFamily: "Arial, sans-serif", backgroundColor: "#fafafa" }}>
                <h2 style={{ marginBottom: "24px" }}>📊 Dashboard Logístico</h2>
                
                <div
                  style={{
                    width: "220px",
                    minWidth: "220px",
                    padding: "16px",
                    paddingBottom: "20px",
                    borderRadius: "10px",
                    // backgroundColor: "#fff",
                    // boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  }}
                >
                  {/* <h4 style={{ margin: "0 0 12px 0", color: FORTIO.navy }}>Filtros</h4> */}
                  <label style={{ fontSize: "14px", color: "#555", display: "block", marginBottom: 8 }}>
                    Periodo
                  </label>
                  <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: "1px solid #d9d9d9",
                      borderRadius: "8px",
                      outline: "none",
                    }}
                  >
                    {PERIOD_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: "flex", gap: "24px", alignItems: "flex-start" }}>
                

                <div style={{ flex: 1 }}>

                {/* Cards com estatísticas */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
                    <StatCard
                        title="Motoristas"
                        value={estatisticas.motoristasCadastrados}
                        icon={FaUserCheck}
                        color={FORTIO.navy}
                    />
                    <StatCard
                        title="Em Viagem"
                        value={estatisticas.caminhoesEmViagem}
                        icon={FaTruck}
                        color={FORTIO.orange}
                    />
                    <StatCard
                        title="Entregas Efetuadas"
                        value={estatisticas.entregasEfetuadas}
                        icon={FaShippingFast}
                        color={FORTIO.navyGreen}
                    />
                    <StatCard
                        title="Entregas a Fazer"
                        value={estatisticas.entregasAFazer}
                        icon={FaClipboardList}
                        color={FORTIO.slate}
                    />
                    <StatCard
                        title="Total de viagens"
                        value={estatisticas.viagensEsteAno}
                        icon={FaCalendarCheck}
                        color={FORTIO.navyDark}
                    />
                    <div style={{ display: "block", flexDirection: "column", height: "20%"}}>
                      <StatCard
                          title="Valor total de fretes"
                          value={estatisticas.valorTotalFretesEsteAno}
                          icon={FaMoneyBillWave}
                          color={FORTIO.orangeDeep}
                      />
                    </div>
                   

                </div>

                {/* Gráficos lado a lado */}
                <div
                    style={{
                        display: "flex",
                        gap: "20px",
                        marginTop: "40px",
                        flexWrap: "wrap",
                    }}
                    >
                    <ChartCard
                        title="Status dos Fretes"
                        style={{ width: "calc(50% - 10px)" }}
                    >
                        <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                            data={dataFretes}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            label={({ name, percent }) =>
                                `${name} (${(percent * 100).toFixed(0)}%)`
                            }
                            >
                            {dataFretes.map((entry, index) => (
                                <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                                />
                            ))}
                            </Pie>
                            <Tooltip />
                            <Legend verticalAlign="bottom" height={36} />
                        </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard
                      title="Entregas (Efetuadas / A Fazer)"
                      style={{ width: "calc(50% - 10px)" }}
                    >
                      <ResponsiveContainer width="100%" height={320}>
                        <PieChart>
                          {/* Gradiente de cor */}
                          <defs>
                            <linearGradient id="colorEfetuadas" x1="0" y1="0" x2="1" y2="1">
                              <stop offset="0%" stopColor="#4CAF50" stopOpacity={0.9} />
                              <stop offset="100%" stopColor="#81C784" stopOpacity={0.8} />
                            </linearGradient>
                            <linearGradient id="colorAFazer" x1="0" y1="0" x2="1" y2="1">
                              <stop offset="100%" stopColor="#FF9800" stopOpacity={0.9} />
                              <stop offset="100%" stopColor="#FFB74D" stopOpacity={0.8} />
                            </linearGradient>
                          </defs>

                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={110}
                            paddingAngle={0}
                            isAnimationActive={true}
                            animationDuration={1000}
                            label={({ name, percent, value }) =>
                              `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                            }
                          >
                            {pieData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={
                                  index === 0 ? "url(#colorEfetuadas)" : "url(#colorAFazer)"
                                }
                                stroke="#fff"
                                strokeWidth={2}
                              />
                            ))}
                          </Pie>

                          {/* Tooltip customizado */}
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "#ffffff",
                              border: "1px solid #ddd",
                              borderRadius: "8px",
                              boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                            }}
                            formatter={(value, name) => [`${value} entregas`, name]}
                          />

                          {/* Legenda */}
                          <Legend
                            verticalAlign="bottom"
                            align="center"
                            iconType="circle"
                            wrapperStyle={{ marginTop: "16px" }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </ChartCard>

                    </div>

                  <ChartCard title="Viagens por tipo de veículo">
                    {loadingTripsByVehicleType ? (
                        <div
                          style={{
                            height: 340,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <span
                            className="spinner-border text-danger"
                            role="status"
                            aria-label="Carregando dados do gráfico"
                          />
                        </div>
                    ) : tripsByVehicleType.length === 0 ? (
                        <p style={{ color: "#666", margin: 0 }}>
                            Nenhum frete com tipo de veículo encontrado.
                        </p>
                    ) : (
                        <ResponsiveContainer width="100%" height={340}>
                            <BarChart
                                data={tripsByVehicleType}
                                margin={{ top: 8, right: 16, left: 8, bottom: 64 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                                <XAxis
                                    dataKey="tipo"
                                    interval={0}
                                    tick={{ fontSize: 11 }}
                                    angle={-28}
                                    textAnchor="end"
                                    height={70}
                                />
                                <YAxis allowDecimals={false} />
                                <Tooltip
                                    formatter={(value) => [`${value} viagens`, "Total"]}
                                    labelFormatter={(label) => `Tipo: ${label}`}
                                />
                                <Bar dataKey="viagens" name="Viagens" radius={[6, 6, 0, 0]}>
                                    {tripsByVehicleType.map((_, index) => (
                                        <Cell
                                            key={`bar-${index}`}
                                            fill={BAR_COLORS[index % BAR_COLORS.length]}
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>

                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "20px",
                    marginTop: "8px",
                  }}
                >
                  <ChartCard
                    title="Origem dos fretes por UF"
                    style={{
                      width: "calc(50% - 10px)",
                      marginTop: 0,
                      flex: "1 1 calc(50% - 10px)",
                      minWidth: "min(100%, 360px)",
                    }}
                  >
                    <BrazilFreightOriginMap countsByUf={originUfCounts} />
                  </ChartCard>
                  <ChartCard
                    title="Destino dos fretes por UF"
                    style={{
                      width: "calc(50% - 10px)",
                      marginTop: 0,
                      flex: "1 1 calc(50% - 10px)",
                      minWidth: "min(100%, 360px)",
                    }}
                  >
                    <BrazilFreightOriginMap
                      countsByUf={destinationUfCounts}
                      palette="red"
                    />
                  </ChartCard>
                </div>

                </div>
                </div>
                </div>

            }/>
            ) : (
                <Navigate to="/login" />
            )}
        
            </>
        );
    };

        const ChartCard = ({ title, children, style = {} }) => (
            <div
            style={{
                marginTop: "40px",
                padding: "24px",
                borderRadius: "10px",
                backgroundColor: "#fff",
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                ...style, // <- isso permite sobrescrever width etc.
            }}
            >
            <h3 style={{ marginBottom: "16px" }}>{title}</h3>
            {children}
            </div>
        );


export default DashboardHome;
